import { Status } from "@/types/Status"
import { Dayjs } from "dayjs"
import { daysFromToday } from "@/helpers/dateHelper"
import {
  ITicketEntities,
  JunTicketStatusText,
  TicketPriority,
} from "./juniferTicket.parser"
import { IJuniferTicket } from "./juniferTicket.types"

const RECENT_INVOLUNTARY_PAYG_SWITCH_WINDOW_DAYS = 21

export class JuniferTicket implements IJuniferTicket {
  id: number
  keyIdentifier: string
  createdDate: Dayjs
  statusText: JunTicketStatusText
  code: string
  name: string
  priority: TicketPriority
  stepCode: string
  identifier: string
  summary: string
  description: string
  team: string
  ticketEntities: ITicketEntities

  constructor(
    id: number,
    keyIdentifier: string,
    createdDate: Dayjs,
    status: JunTicketStatusText,
    code: string,
    name: string,
    priority: TicketPriority,
    stepCode: string,
    identifier: string,
    summary: string,
    description: string,
    team: string,
    ticketEntities: ITicketEntities,
  ) {
    this.id = id
    this.keyIdentifier = keyIdentifier
    this.createdDate = createdDate
    this.code = code
    this.name = name
    this.statusText = status
    this.priority = priority
    this.stepCode = stepCode
    this.identifier = identifier
    this.summary = summary
    this.description = description
    this.team = team
    this.ticketEntities = {
      accounts: ticketEntities.accounts || [],
      meterPoints: ticketEntities.meterPoints || [],
      paymentSchedulePeriods: ticketEntities.paymentSchedulePeriods || [],
      customers: ticketEntities.customers || [],
      tickets: ticketEntities.tickets || [],
      communications: ticketEntities.communications || [],
      bills: ticketEntities.bills || [],
      paymentRequests: ticketEntities.paymentRequests || [],
      paymentSchedulePeriod: ticketEntities.paymentSchedulePeriod || [],
      paymentMethods: ticketEntities.paymentMethods || [],
      alerts: ticketEntities.alerts || [],
      billPeriods: ticketEntities.billPeriods || [],
      billingEntities: ticketEntities.billingEntities || [],
      billEmails: ticketEntities.billEmails || [],
      changeOfTenancys: ticketEntities.changeOfTenancys || [],
      assets: ticketEntities.assets || [],
      contacts: ticketEntities.contacts || [],
    }
  }

  get status(): Status {
    switch (this.statusText) {
      case "Open":
        return new Status("caution", this.statusText)
      case "Error":
        return new Status("critical", this.statusText)
      default:
        return new Status("default", this.statusText)
    }
  }

  get isOpen(): boolean {
    return this.statusText === "Open"
  }

  get isWithinRenewalPeriod(): boolean {
    return (
      !this.keyIdentifier.includes("COMPLAINTS-TICKET") &&
      this.keyIdentifier.includes("RENEW-LETTER") &&
      this.statusText === "Open"
    )
  }

  get isOpenOperationalTicket(): boolean {
    return (
      !this.keyIdentifier.includes("COMPLAINTS-TICKET") &&
      this.statusText === "Open"
    )
  }

  get isPhotoRequestTicket(): boolean {
    return (
      this.isOpenOperationalTicket &&
      this.keyIdentifier.includes("Photo Request") &&
      this.stepCode !== "Response Received [A5]"
    )
  }

  get isOpenInvoluntaryPaygTicket(): boolean {
    return (
      this.keyIdentifier.includes("InvoluntaryPAYG") &&
      this.statusText === "Open"
    )
  }

  get isRecentInvoluntaryPaygSwitch(): boolean {
    return (
      daysFromToday(this.createdDate) <=
      RECENT_INVOLUNTARY_PAYG_SWITCH_WINDOW_DAYS
    )
  }
}
